'use client'

import RightIcon from '@/icons/RightIcon'
import { ChatLink } from '@/lib/editor/link-utils'
import { cn } from '@/lib/utils'
import { Placeholder } from '@tiptap/extension-placeholder'
import { EditorContent, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { FlagIcon } from 'lucide-react'
import Image from 'next/image'
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { FiPaperclip } from 'react-icons/fi'

type GraphicChatInputProps = {
  onSendMessage: (message: string, options: { files?: File[] }) => Promise<void>
  disabled?: boolean
  isLoading?: boolean
  placeholder?: string
  defaultValue?: string
  contextComponent?: ReactNode
  enableFileUpload?: boolean
  enableReportButton?: boolean
  className?: string
}

export default function GraphicChatInput({
  onSendMessage,
  disabled = false,
  isLoading = false,
  placeholder = 'Type your message...',
  defaultValue = '',
  contextComponent,
  enableFileUpload = true,
  enableReportButton = true,
  className,
}: GraphicChatInputProps) {
  const [isFocus, setIsFocus] = useState<boolean>(false)
  const [isInputEmpty, setIsInputEmpty] = useState(true)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)

  // Tiptap editor
  const editor = useEditor({
    immediatelyRender: false, // Fix SSR hydration mismatch
    extensions: [
      StarterKit.configure({
        // Disable features we don't need for chat input
        heading: false,
        blockquote: false,
        horizontalRule: false,
        listItem: false,
        orderedList: false,
        bulletList: false,
        codeBlock: false,
        code: false,
        strike: false,
        italic: false,
        bold: false,
      }),
      Placeholder.configure({
        placeholder: disabled ? 'Sending...' : placeholder,
      }),
      ChatLink,
    ],
    content: defaultValue,
    editable: !disabled,
    onUpdate: ({ editor }) => {
      const text = editor.getText()
      setIsInputEmpty(text.trim() === '')
    },
    onFocus: () => setIsFocus(true),
    onBlur: () => setIsFocus(false),
    onCreate: () => {
      // Set initial empty state
      setIsInputEmpty(true)
    },
    editorProps: {
      handleKeyDown: (view, event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
          event.preventDefault()
          const text = editor?.getText() || ''
          if ((text.trim() || file) && !disabled) {
            void handleSubmit(text, file)
          }
          return true
        }
        return false
      },
    },
  })

  // Update editor editable state when disabled prop changes
  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled)
    }
  }, [editor, disabled])

  // Cleanup editor on unmount
  useEffect(() => {
    return () => {
      editor?.destroy()
    }
  }, [editor])

  const handlePaperclipClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.value = '' // allow re-uploading same file
      fileInputRef.current.click()
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setFile(file)
    }
  }

  const handleRemoveFile = () => {
    setFile(null)
  }

  const handleSubmit = useCallback(
    async (text: string, file: File | null) => {
      if (disabled || (!text.trim() && !file)) return

      const message = text || editor?.getText() || ''
      const options = {
        files: file ? [file] : [],
      }

      try {
        await onSendMessage(message, options)
        editor?.commands.clearContent()
        setIsInputEmpty(true)
        setFile(null)
      } catch (error) {
        console.error('Failed to send message:', error)
        // Let the error event handler manage the status
      }
    },
    [onSendMessage, disabled, editor, file]
  )

  const canSubmit = (!isInputEmpty || file) && !disabled && !isLoading

  return (
    <div
      className={cn(
        'box-border flex flex-col items-start rounded-2xl border border-[#E0E0E0] bg-[#F3F3F3]',
        'order-1 max-h-96 flex-none flex-grow-0 gap-2 self-stretch overflow-y-auto p-4',
        { 'border-[#B0B0B0]': isFocus },
        { 'cursor-not-allowed opacity-60': disabled },
        className
      )}
      style={{ transition: 'border-color 0.2s, opacity 0.2s' }}
    >
      {/* File preview */}
      {file && (
        <div className="mb-2 flex max-w-full items-center gap-2 rounded bg-white p-2 shadow-sm">
          {file.type.startsWith('image/') ? (
            <Image
              src={URL.createObjectURL(file)}
              alt={file.name}
              className="h-10 w-10 rounded object-cover"
            />
          ) : (
            <div className="flex h-10 w-10 items-center justify-center rounded bg-gray-200">
              <span className="text-xs text-gray-600">
                {file.name.split('.').pop()?.toUpperCase() || 'FILE'}
              </span>
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium text-gray-800">{file.name}</div>
            <div className="truncate text-xs text-gray-500">{(file.size / 1024).toFixed(1)} KB</div>
          </div>
          <button
            type="button"
            className="ml-2 text-gray-400 hover:text-red-500"
            aria-label="Remove file"
            onClick={handleRemoveFile}
          >
            ×
          </button>
        </div>
      )}

      {/* Context component */}
      {contextComponent}

      {/* Input row */}
      <div className="flex w-full items-center">
        <div className="relative w-full">
          <div
            className="[&_.ProseMirror]:font-hanken-grotesk w-full border-none bg-transparent text-base text-[#222] shadow-none focus:outline-none focus:ring-0 [&_.ProseMirror]:min-h-[20px] [&_.ProseMirror]:break-words [&_.ProseMirror]:p-0 [&_.ProseMirror]:leading-normal [&_.ProseMirror]:outline-none"
            style={{ minHeight: '20px' }}
          >
            <EditorContent editor={editor} className="w-full" />
          </div>
        </div>
      </div>

      {/* Bottom bar: Actions and Send button */}
      <div className="flex w-full items-center gap-x-2">
        {enableFileUpload && (
          <button
            className="flex h-6 w-6 cursor-pointer items-center justify-center rounded-full transition-colors hover:bg-[#E0E0E0] focus:bg-[#E0E0E0]"
            aria-label="Attach file"
            data-tooltip-id="attach-file-tooltip"
            data-tooltip-content="Attach file"
            tabIndex={0}
            role="button"
            onClick={handlePaperclipClick}
          >
            <FiPaperclip className="text-[#757575]" />
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={handleFileChange}
              tabIndex={-1}
              disabled={disabled}
            />
          </button>
        )}

        {enableReportButton && (
          <button
            className="flex h-6 w-6 cursor-pointer items-center justify-center rounded-full transition-colors hover:bg-[#E0E0E0] focus:bg-[#E0E0E0]"
            aria-label="Report issue"
            data-tooltip-id="report-issue-tooltip"
            data-tooltip-content="Report issue"
            tabIndex={0}
            role="button"
          >
            <FlagIcon className="h-4 w-4 text-[#757575]" />
          </button>
        )}

        <span className="flex-1"></span>

        <button
          className={cn(
            'flex h-8 w-8 items-center justify-center rounded-full transition-colors',
            !canSubmit
              ? 'cursor-not-allowed bg-[#E0E0E0] opacity-60'
              : isFocus
                ? 'bg-[#333] hover:bg-[#444]'
                : 'bg-[#222] hover:bg-[#444]'
          )}
          onClick={() => {
            void handleSubmit(editor?.getText() || '', file)
          }}
          aria-label="Send message"
          style={{ boxShadow: '0 1px 4px 0 rgba(0,0,0,0.08)' }}
          disabled={!canSubmit}
          tabIndex={isInputEmpty ? -1 : 0}
        >
          {isLoading ? (
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
          ) : (
            <RightIcon className="text-white" />
          )}
        </button>
      </div>
    </div>
  )
}
